import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../../api/stats.js';
import { LineChart } from '../../components/LineChart.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { money, num } from '../../lib/format.js';

/**
 * 한 달을 놓고 보는 상세. lottery(F주문통계(상세)) 전용이다.
 *
 * randombox 판(N주문통계(상세), 원본 randombox_order_stat_list.php)은 메뉴와
 * 함께 이 화면·로직 자체를 없앴다 - N주문통계(개요)/N랜덤박스별상세는 그대로다.
 *
 * 원본은 같은 달로 그래프를 넷 그리는데, 일별 매출·판매매수는 같은 자료라
 * 하나로 겹쳐 그린다. 축이 달라서 계열을 나눠 두 그래프로 뒀다.
 *
 * 개요 화면과 달리 매출이 실제 결제금액(total_amount)이다. 원본이 그렇다.
 */

const TITLE = 'F주문통계(상세)';

function thisMonth() {
  return new Date().toISOString().slice(0, 7);
}

export function MonthDetailPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? thisMonth();

  const detail = useQuery({
    queryKey: ['stats', 'lottery', 'month', month],
    queryFn: () => statsApi.month('lottery', month),
  });

  const charts = useMemo(() => {
    const rows = detail.data?.daily ?? [];
    const labels = rows.map((d) => d.day);
    return {
      labels,
      sales: [{ id: 'sales', label: '매출', points: rows.map((d) => ({ x: d.day, y: d.sales })) }],
      tickets: [
        { id: 'tickets', label: '판매매수', points: rows.map((d) => ({ x: d.day, y: d.tickets })) },
      ],
    };
  }, [detail.data]);

  const totals = useMemo(() => {
    const rows = detail.data?.byBox ?? [];
    return {
      sales: rows.reduce((n, r) => n + r.sales, 0),
      tickets: rows.reduce((n, r) => n + r.tickets, 0),
    };
  }, [detail.data]);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">{TITLE}</h1>
          <p className="page-desc">한 달치 일별 추이와 랜덤박스별 매출.</p>
        </div>
        <div className="page-head__actions">
          {detail.isFetching && <span className="page-head__busy">갱신 중…</span>}
        </div>
      </header>

      <div className="filters">
        <Field label="조회 월">
          <input
            className="input"
            type="month"
            value={month}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set('month', e.target.value);
              setParams(next, { replace: true });
            }}
          />
        </Field>
      </div>

      {detail.isPending ? (
        <Spinner />
      ) : detail.isError ? (
        <ErrorBox error={detail.error} onRetry={() => void detail.refetch()} />
      ) : detail.data.daily.length === 0 ? (
        <EmptyBox>이 달에는 주문이 없습니다.</EmptyBox>
      ) : (
        <>
          <div className="summary">
            <div className="summary__item">
              <span className="summary__label">매출</span>
              <strong className="summary__value">{money(totals.sales)}</strong>
            </div>
            <div className="summary__item">
              <span className="summary__label">판매매수</span>
              <strong className="summary__value">{num(totals.tickets)}</strong>
            </div>
          </div>

          <div className="panel">
            <h2 className="panel__title">일별 매출</h2>
            <LineChart labels={charts.labels} series={charts.sales} format={money} />
          </div>

          <div className="panel">
            <h2 className="panel__title">일별 판매매수</h2>
            <LineChart labels={charts.labels} series={charts.tickets} />
          </div>

          <div className="panel">
            <h2 className="panel__title">랜덤박스별 매출</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>랜덤박스</th>
                    <th style={{ textAlign: 'right' }}>판매매수</th>
                    <th style={{ textAlign: 'right' }}>매출</th>
                    <th style={{ textAlign: 'right' }}>비중</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.byBox.map((b) => (
                    <tr key={b.id}>
                      <td>{b.name ?? b.id}</td>
                      <td style={{ textAlign: 'right' }}>{num(b.tickets)}</td>
                      <td style={{ textAlign: 'right' }}>{money(b.sales)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {totals.sales ? `${((b.sales / totals.sales) * 100).toFixed(1)}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
